"use client";

import { ArrowLeft } from "lucide-react";
import { useEffect, useRef } from "react";

import { NavPanelShell } from "@/components/nav/NavPanel";
import NavSearchResults from "@/components/nav/NavSearchResults";
import {
  NAV_ARRIVE,
  NAV_COLUMN_WIDTH,
  NAV_FIELD_LIVE,
  NAV_SCROLLBAR,
  SEARCH_TILE,
  arriveStep,
} from "@/components/nav/nav-styles";
import { Kbd } from "@/components/ui/kbd";
import { cn } from "@/lib/utils";

const HINTS = [
  ["↑↓", "move"],
  ["↵", "open"],
  ["esc", "close"],
];

/**
 * Search beside the icon rail: the input sits in the top bar right above the
 * results (`inputSlot` is the bar's slot, at the pane's width, with a back
 * arrow where the field's icon was) and the results open in the panel's
 * place, as wide as Sabine's chat, with the keys that drive them at their
 * foot. It opens from the field, ⌘K, or typing at the nav, and closes on
 * Escape, the back arrow, or choosing a result; the menu the panel showed
 * comes back as it was. Escape and the arrow hand focus back to the field.
 */
export default function NavSearchPane({ search, open, apple, inputSlot }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) search.focusInput(inputRef.current);
  }, [open]);

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      search.dismiss();
    }
  };

  return (
    <NavPanelShell open={open} width={NAV_COLUMN_WIDTH} label="Search">
      <NavSearchResults
        search={search}
        inputRef={inputRef}
        apple={apple}
        onKeyDown={onKeyDown}
        inputSlot={inputSlot}
        inputWrapperClassName={NAV_FIELD_LIVE}
        inputClassName="h-full py-0"
        inputLeading={
          <button
            type="button"
            aria-label="Close search"
            onClick={search.dismiss}
            className={cn("h-7 w-7 rounded-lg", SEARCH_TILE)}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        }
        listClassName={`max-h-none flex-1 px-1 pb-2 ${NAV_SCROLLBAR}`}
      />
      <div
        className={cn(
          NAV_ARRIVE,
          "flex shrink-0 gap-3 border-t border-stone-200/70 px-4 py-2 text-[11px] text-gray-500",
        )}
        style={arriveStep(search.groupedResults.length)}
      >
        {HINTS.map(([key, action]) => (
          <span key={action} className="flex items-center gap-1">
            <Kbd>{key}</Kbd>
            {action}
          </span>
        ))}
      </div>
    </NavPanelShell>
  );
}
